import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { EmployeeForm } from "../../components/form/employeeForm";
import { EmployeeMaterialStatus, EmployeeService } from "../../api/generated";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { DistributionStatusIcon } from "../../components/icon/distribution_status_icon";

export const Route = createFileRoute("/employee/$employeeId/")({
  loader: async ({ params }) => {
    const employeeId = Number(params.employeeId);
    return await EmployeeService.getEmployeeById(employeeId);
  },
  component: RouteComponent,
});

type MaterialStatus =
  | { status: "LOADING" }
  | { status: "READY"; materialStatus: EmployeeMaterialStatus[] }
  | { status: "ERROR"; error: Error };

function RouteComponent() {
  const employee = Route.useLoaderData();

  const [materialStatus, setMaterialStatus] = useState<MaterialStatus>({
    status: "LOADING",
  });

  const navigator = useNavigate();

  const returnToEmployees = () => {
    navigator({ to: "/employee" });
  };

  const loadMaterialStatus = async () => {
    setMaterialStatus({ status: "LOADING" });
    try {
      if (!employee.id) {
        setMaterialStatus({
          status: "ERROR",
          error: new Error("Employee ID is missing"),
        });
        return;
      }
      const materialStatus = await EmployeeService.getEmployeeMaterialStatus(
        employee.id,
      );
      console.log("Fetched material status:", materialStatus);
      setMaterialStatus({ status: "READY", materialStatus });
    } catch (error) {
      setMaterialStatus({ status: "ERROR", error: error as Error });
    }
  };

  useEffect(() => {
    loadMaterialStatus();
  }, []);

  return (
    <div className="flex justify-center items-center mt-10">
      <div className="flex w-3/4 flex-col items-center">
        <h1 className="text-2xl font-bold mb-4">
          {employee.firstName} {employee.lastName}
        </h1>
        <EmployeeForm employee={employee} onDelete={returnToEmployees} />

        <div className="flex flex-row justify-between items-center">
          <h2 className="ml-4">Huidige status</h2>

          <Link
            to="/employee/$employeeId/schedule"
            className="btn"
            params={{ employeeId: employee.id!.toString() }}
          >
            Materiaal rooster aanpassen
          </Link>
        </div>

        {materialStatus.status === "LOADING" && (
          <p>Loading material status...</p>
        )}
        {materialStatus.status === "ERROR" && (
          <p>Error loading material status: {materialStatus.error.message}</p>
        )}
        {materialStatus.status === "READY" && (
          <div className="tabs tabs-lift">
            {materialStatus.materialStatus.map((status, index) => (
              <>
                <input
                  type="radio"
                  name={`distribution-tab`}
                  className="tab"
                  aria-label={status.date}
                  defaultChecked={index === 0}
                />
                <div className="tab-content bg-base-100 border-base-300 p-6 overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Materiaal</th>
                        <th>Benodigde hoeveelheid</th>
                        <th>Uitgegeven</th>
                        <th>Nog uit te geven</th>
                        <th>Status</th>
                        <th>Retouren</th>
                      </tr>
                    </thead>
                    <tbody>
                      {status.distributionStatus.length > 0 &&
                        status.distributionStatus.map(
                          (distributionStatus, distributionIndex) => (
                            <tr key={`${distributionIndex}-${status.date}`}>
                              <td>{distributionStatus.materialName}</td>
                              <td>{distributionStatus.scheduledQuantity}</td>
                              <td>{distributionStatus.distributedQuantity}</td>
                              <td>{distributionStatus.pendingQuantity}</td>
                              <td>
                                <DistributionStatusIcon
                                  pendingQuantity={
                                    distributionStatus.pendingQuantity
                                  }
                                  requiredQuantity={
                                    distributionStatus.scheduledQuantity
                                  }
                                />
                              </td>
                              <td>{distributionStatus.returnedQuantity}</td>
                            </tr>
                          ),
                        )}
                      {status.distributionStatus.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center">
                            Geen materiaal gepland
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
